import { z } from 'zod'

import { DateZod, GeoPointZod } from '../common'
import { DateStringZod } from '../common/date-string-zod'
import { CostFormulaZod } from '../configuration/configuration-automated-assignment-minutes-zod'
import { LocalizedValueZod } from '../localization'
import { PromotionPlacementZod } from '../promotions/promotions-zod'

import { PiloteEveningStatusZod } from './pilote'
import { RegionInfoZod } from './yapla'

export const DEFAULT_REGION_TIMEZONE = 'America/Toronto'

export const LocationOperationDateZod = z.object({
  date: z.string(),
  startTime: z.string(),
  startTimestamp: DateZod,
  endTime: z.string(),
  endTimestamp: DateZod,
  eveningId: z.number(),
})

export type WaitTime = z.infer<typeof WaitTimeZod>
export const WaitTimeZod = z.object({
  minimumInMinutes: z.number(),
  maximumInMinutes: z.number(),
  averageInMinutes: z.number(),
  exceedsThreshold: z.boolean(),
})

export type WaitTimeConfiguration = z.infer<typeof WaitTimeConfigurationZod>
export const WaitTimeConfigurationZod = z.object({
  type: z.enum(['default', 'increased']).optional().default('default'),
  averageInMinutes: z.number().default(45),
  standardDeviationInMinutes: z.number().default(15),
  multipliers: z.record(z.enum(['default', 'increased']), z.number()).default({}),
  thresholdInMinutes: z.number().optional(),
})

const RegionCommunityZod = z.object({
  id: z.number(),
  name: z.string(),
  geolocation: GeoPointZod,
})

const EveningZod = z.object({
  date: DateZod,
  status: PiloteEveningStatusZod,
  eveningId: z.number().optional(),
})

export const AuxRoleZod = z.enum([
  'accueil',
  'alimentation',
  'vehicules',
  'formateur',
  'equipes',
  'formulaires',
  'groupes',
  'inscription',
  'equipePromo',
  'kilometrage',
  'repartition',
  'telephoniste',
  'tresorie',
  'comiteOrganisateur',
  'personnificateur',
  'supportInformatique',
])
export type AuxRole = z.infer<typeof AuxRoleZod>

export const CentralRoleZod = z.object({
  id: z.number(),
  legacyCode: z.string().nullish().default(null),
  label: LocalizedValueZod,
  description: z
    .object({
      fr: z.string().nullish().default(null),
      en: z.string().nullish().default(null),
    })
    .nullish()
    .default({ fr: null, en: null }),
})
export type CentralRole = z.infer<typeof CentralRoleZod>

const RETIRED_REGION_ASSIGNMENTS = { trf: 'solver', legacy: 'manual' } as const

// A region document written before the `trf` and `legacy` types were retired must stay readable until it is migrated.
export const RegionAssignmentZod = z
  .enum(['manual', 'solver', 'trf', 'legacy'])
  .transform((value): 'manual' | 'solver' =>
    value === 'trf' || value === 'legacy' ? RETIRED_REGION_ASSIGNMENTS[value] : value
  )
export type RegionAssignment = z.infer<typeof RegionAssignmentZod>

export const RegionZod = z.object({
  active: z.boolean().optional().default(false),
  test: z.boolean().default(false),
  visibleInMobileApp: z.boolean().default(false),
  yaplaId: z.string(),
  piloteId: z.number().optional(),
  cities: z.array(z.string()).optional().default([]),
  citiesExclusions: z.array(z.string()).optional().default([]),
  locations: z
    .record(
      z.object({
        name: z.string().optional().default(''),
        addressComponentNames: z.array(z.string()).optional().default([]),
        placeIds: z.array(z.string()).optional().default([]),
      })
    )
    .optional()
    .default({}),
  qualifiedCities: z.array(z.string()).nullish().default([]),
  regionInfo: RegionInfoZod.optional(),
  name: z.string().optional().default(''),
  mobileTransportRequestsEnabled: z.boolean().optional().default(false),
  openForTransportRequests: z.boolean().optional().default(false),
  currentOperatingDate: LocationOperationDateZod.optional(),
  nextOperatingDate: LocationOperationDateZod.optional(),
  sponsoredImages: z
    .object({
      customerAppImageUrl: LocalizedValueZod.optional(),
      customerAppBottomImageUrl: LocalizedValueZod.optional(),
    })
    .optional(),
  maximumDurationTimeInMinutes: z.number().optional().default(60),
  waitTimeConfiguration: WaitTimeConfigurationZod.optional(),
  waitTime: WaitTimeZod.optional(),
  isElectronicPaymentEnabled: z.boolean().default(false),
  isAutomatedAssignmentEnabled: z.boolean().default(false),
  isTurboAssignmentEnabled: z.boolean().nullish().default(false),
  isManualWaitTimeEnabled: z.boolean().nullish().default(false),
  isPostTerritoryEnabled: z.boolean().nullish().default(false),
  assignment: RegionAssignmentZod.optional().default('manual'),
  // eslint-disable-next-line unicorn/prefer-top-level-await -- Zod's catch: an unknown formula reads as points
  costFormula: CostFormulaZod.nullish().default('points').catch('points'),
  isRegistrationOpen: z.boolean().optional().default(false),
  isAvailabilitiesRequired: z.boolean().nullish().default(false),
  isOdometerPhotoRequired: z.boolean().nullish().default(false),
  currentCampaignId: z.number().nullable().optional(),
  timezone: z.string().optional().default(DEFAULT_REGION_TIMEZONE),
  mapConfiguration: z
    .object({
      zoom: z.number().optional(),
      ne: GeoPointZod,
      sw: GeoPointZod,
      center: GeoPointZod,
    })
    .optional(),
  basePhoneNumbers: z.record(z.string(), z.string()).optional().default({}),
  communities: RegionCommunityZod.array().optional().default([]),
  evenings: EveningZod.array().optional().default([]),
  overrides: z
    .object({
      alwaysOperating: z.boolean().optional().default(false),
      prepareAutomatedAssignmentPlan: z.boolean().optional().default(false),
    })
    .optional()
    .default({}),
  roles: z.array(AuxRoleZod).optional().default([]),
  centralRoles: z.array(CentralRoleZod).nullish().default([]),
  promotionPlacements: z.record(PromotionPlacementZod.array()).optional().default({}),
  promotionPlacementLocks: z.record(z.string(), z.record(PromotionPlacementZod, DateZod)).nullish().default({}),
  promotionMilestoneDates: z.record(z.record(DateStringZod, z.string().nullable())).optional().default({}),
})
export type Region = z.infer<typeof RegionZod>
